import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiPlus, FiEdit, FiTrash2, FiUsers, FiTarget } from 'react-icons/fi';
import { formatINR } from '../../utils/currency';
import Swal from 'sweetalert2';

const IncentiveManagement = () => {
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedIncentive, setSelectedIncentive] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'sales',
    target: '',
    incentiveAmount: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    fetchIncentives();
  }, []);

  const fetchIncentives = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API endpoint
      // Mock data
      setIncentives([
        {
          id: 1,
          name: 'Monthly Sales Bonus',
          type: 'sales',
          target: '50000',
          incentiveAmount: '5000',
          description: 'Bonus for achieving monthly sales target',
          status: 'active',
          employees: 25
        },
        {
          id: 2,
          name: 'Production Efficiency Reward',
          type: 'production',
          target: '95%',
          incentiveAmount: '3000',
          description: 'Reward for maintaining production efficiency above 95%',
          status: 'active',
          employees: 15
        }
      ]);
    } catch (error) {
      console.error('Error fetching incentives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // TODO: Replace with actual API endpoint
      Swal.fire('Success', selectedIncentive ? 'Incentive updated successfully' : 'Incentive created successfully', 'success');
      setShowModal(false);
      setSelectedIncentive(null);
      setFormData({
        name: '',
        type: 'sales',
        target: '',
        incentiveAmount: '',
        description: '',
        status: 'active'
      });
      fetchIncentives();
    } catch (error) {
      console.error('Error saving incentive:', error);
      Swal.fire('Error', 'Failed to save incentive', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        // TODO: Replace with actual API endpoint
        Swal.fire('Deleted', 'Incentive has been deleted', 'success');
        fetchIncentives();
      } catch (error) {
        console.error('Error deleting incentive:', error);
        Swal.fire('Error', 'Failed to delete incentive', 'error');
      }
    }
  };

  const handleEdit = (incentive) => {
    setSelectedIncentive(incentive);
    setFormData({
      name: incentive.name,
      type: incentive.type,
      target: incentive.target,
      incentiveAmount: incentive.incentiveAmount,
      description: incentive.description,
      status: incentive.status
    });
    setShowModal(true);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiDollarSign className="text-blue-600" />
            Incentive Management
          </h1>
          <p className="text-gray-600 mt-1">Manage employee incentives and rewards</p>
        </div>
        <button
          onClick={() => {
            setSelectedIncentive(null);
            setFormData({
              name: '',
              type: 'sales',
              target: '',
              incentiveAmount: '',
              description: '',
              status: 'active'
            });
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> New Incentive
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {incentives.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              No incentives found
            </div>
          ) : (
            incentives.map((incentive) => (
              <div key={incentive.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">{incentive.name}</h3>
                    <p className="text-sm text-gray-600 capitalize">{incentive.type}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    incentive.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {incentive.status}
                  </span>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <FiTarget className="text-gray-400" />
                    <span className="text-gray-600">Target: <strong>{incentive.target}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiDollarSign className="text-gray-400" />
                    <span className="text-gray-600">Amount: <strong>{formatINR(incentive.incentiveAmount)}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiUsers className="text-gray-400" />
                    <span className="text-gray-600">Eligible: <strong>{incentive.employees} employees</strong></span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">{incentive.description}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(incentive)}
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium"
                  >
                    <FiEdit className="inline mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(incentive.id)}
                    className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {selectedIncentive ? 'Edit Incentive' : 'New Incentive'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Incentive Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sales">Sales</option>
                    <option value="production">Production</option>
                    <option value="quality">Quality</option>
                    <option value="attendance">Attendance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                  <input
                    type="text"
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 50000 or 95%"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Incentive Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.incentiveAmount}
                    onChange={(e) => setFormData({ ...formData, incentiveAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedIncentive(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {selectedIncentive ? 'Update' : 'Create'} Incentive
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncentiveManagement;

